/**
 * Every setting in docs/spec/04 §6 as a row: how it shows, how left/right change it, and one line
 * of explanation. Presentation rows settle the preset (Custom unless they still match one). Pure.
 */

import type { Options, Preset } from '../../platform/options';
import { settlePreset, withPreset } from '../../platform/options';
import { wrapIndex } from '../kit/menuModel';
import type { RowValue } from '../kit/optionRow';

export interface OptionRow {
  readonly id: string;
  readonly group: string;
  readonly label: string;
  /** One line, at most 26 characters. */
  readonly help: string;
  value(o: Options): RowValue;
  /** Left (-1) or right (+1); absent for a row that opens another screen. */
  step?(o: Options, delta: number): Options;
  /** Set a level row straight to a value (clicking its bar). */
  set?(o: Options, value: number): Options;
}

type Key = Exclude<keyof Options, 'keys'>;

function choice<K extends Key>(key: K, values: readonly Options[K][], labels: readonly string[], look = false) {
  return {
    value: (o: Options): RowValue => ({ kind: 'choice', text: labels[values.indexOf(o[key])] ?? '?' }),
    step: (o: Options, delta: number): Options => {
      const next = values[wrapIndex(values.indexOf(o[key]) + delta, values.length)] ?? o[key];
      const changed: Options = { ...o, [key]: next };
      // A presentation setting changed by hand: the preset follows (04 §6).
      return look ? settlePreset(changed) : changed;
    },
  };
}

type Flag = 'particles' | 'shake' | 'deepStars' | 'sidePanels' | 'turbo' | 'reduceFlashing';

/** Default on. */
function onOff(key: Flag, look = false) {
  return choice(key, [true, false], ['ON', 'OFF'], look);
}

/** Default off. */
function offOn(key: Flag) {
  return choice(key, [false, true], ['OFF', 'ON']);
}

function level(key: 'master' | 'sfx' | 'music') {
  const clamp = (v: number): number => Math.max(0, Math.min(10, Math.round(v)));
  return {
    value: (o: Options): RowValue => ({ kind: 'level', value: o[key], max: 10 }),
    step: (o: Options, delta: number): Options => ({ ...o, [key]: clamp(o[key] + delta) }),
    set: (o: Options, v: number): Options => ({ ...o, [key]: clamp(v) }),
  };
}

const PRESETS: readonly Preset[] = ['enhanced', 'authentic', 'custom'];
const PRESET_NAMES: Readonly<Record<Preset, string>> = {
  enhanced: 'ENHANCED',
  authentic: 'ARCADE AUTHENTIC',
  custom: 'CUSTOM',
};

const P = 'PRESENTATION';
const A = 'AUDIO';
const G = 'GAMEPLAY';
const X = 'ACCESSIBILITY';
const C = 'CONTROLS';

export const OPTION_ROWS: readonly OptionRow[] = [
  {
    id: 'preset', group: P, label: 'PRESET', help: 'ALL THE EXTRAS, OR AS 1981',
    value: (o) => ({ kind: 'choice', text: PRESET_NAMES[o.preset] }),
    step: (o, d) => withPreset(o, PRESETS[wrapIndex(PRESETS.indexOf(o.preset) + d, PRESETS.length)] ?? 'enhanced'),
  },
  { id: 'particles', group: P, label: 'PARTICLES', help: 'SPARKS, EXHAUST AND EMBERS', ...onOff('particles', true) },
  { id: 'shake', group: P, label: 'SCREEN SHAKE', help: 'A JOLT ON THE BIG BLASTS', ...onOff('shake', true) },
  {
    id: 'deepStars', group: P, label: 'DEEP STARFIELD', help: 'A DEEPER SKY, AND STREAKS',
    ...onOff('deepStars', true),
  },
  {
    id: 'sidePanels', group: P, label: 'SIDE PANELS', help: 'GAUGES BESIDE THE SCREEN',
    ...onOff('sidePanels', true),
  },
  {
    id: 'crt', group: P, label: 'DISPLAY', help: 'TUBE GLOW, FULL CRT OR OFF',
    ...choice('crt', ['soft', 'arcade', 'off'], ['SOFT', 'ARCADE', 'OFF'], true),
  },
  {
    id: 'scale', group: P, label: 'SCALING', help: 'CRISP PIXELS OR FIT WINDOW',
    ...choice('scale', ['pixel', 'fit'], ['PIXEL-PERFECT', 'FIT']),
  },
  { id: 'master', group: A, label: 'MASTER', help: 'OVERALL VOLUME', ...level('master') },
  { id: 'sfx', group: A, label: 'EFFECTS', help: 'SOUND EFFECTS VOLUME', ...level('sfx') },
  { id: 'music', group: A, label: 'MUSIC', help: 'MUSIC VOLUME', ...level('music') },
  { id: 'turbo', group: G, label: 'TURBO FIRE', help: 'HOLD FIRE TO KEEP SHOOTING', ...offOn('turbo') },
  {
    id: 'reduceFlashing', group: X, label: 'REDUCE FLASHING', help: 'NO FLASHES, STEADY STARS',
    ...offOn('reduceFlashing'),
  },
  {
    id: 'controls', group: C, label: 'CONTROLS', help: 'REBIND KEYS; PAD AND TOUCH',
    value: () => ({ kind: 'link' }),
  },
  {
    id: 'touch', group: C, label: 'TOUCH CONTROLS', help: 'ON-SCREEN PAD: AUTO/ON/OFF',
    ...choice('touch', ['auto', 'on', 'off'], ['AUTO', 'ON', 'OFF']),
  },
];

/** A line of the list: a group heading or a setting. */
export type OptionLine =
  | { readonly kind: 'group'; readonly label: string; readonly y: number }
  | { readonly kind: 'row'; readonly row: number; readonly y: number };

/** The rows with a heading before each group, each with its y: `pitch` apart, `gap` more above a heading. */
export function optionLines(
  top: number, pitch: number, gap: number, rows: readonly OptionRow[] = OPTION_ROWS,
): OptionLine[] {
  const lines: OptionLine[] = [];
  let y = top;
  rows.forEach((row, i) => {
    if (rows[i - 1]?.group !== row.group) {
      if (i > 0) y += gap;
      lines.push({ kind: 'group', label: row.group, y });
      y += pitch;
    }
    lines.push({ kind: 'row', row: i, y });
    y += pitch;
  });
  return lines;
}

/** The row whose line is under `y` (each line owns `pitch` pixels from 2 above its text), or -1. */
export function rowAtY(lines: readonly OptionLine[], pitch: number, y: number): number {
  for (const line of lines) {
    if (line.kind === 'row' && y >= line.y - 2 && y < line.y - 2 + pitch) return line.row;
  }
  return -1;
}

/** What a row shows, in words, for a screen reader. */
export function valueText(v: RowValue): string {
  return v.kind === 'choice' ? v.text : v.kind === 'level' ? `${v.value} of ${v.max}` : 'opens a screen';
}
